import { ArrowLeft, MoreHorizontal, Settings2, Trash2 } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { CopyButton, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { CampaignCadence } from "@/features/campaign-cadence";
import { CampaignDialing } from "@/features/campaign-dialing";
import { CampaignDispositions } from "@/features/campaign-dispositions";
import { CampaignFormDialog } from "@/features/campaign-form";
import { CampaignLines } from "@/features/campaign-lines";
import { CampaignRepNumbers } from "@/features/campaign-rep-numbers";
import { CampaignLists } from "@/features/campaign-lists";
import { CampaignStaffing } from "@/features/campaign-staffing";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  CAMPAIGN_STATUSES, CAMPAIGN_TABS, type CampaignTab, dialModeText,
  dialingContradictsPurpose, purposeText, statusText, visibleTabs,
} from "@/lib/campaign";
import { useCampaign, useDeleteCampaign, useUpdateCampaign } from "@/lib/queries";
import { atLeast } from "@/lib/types";

/**
 * One campaign, and everything about how it is worked (ADR-014, -017, -019).
 *
 * Tabs rather than one long form, because the halves change at different times: routing and
 * staffing when the floor changes, dispositions when the firm's reporting does, dialing and lists
 * when a batch of leads arrives. Each tab saves on its own, so a half-edited dialing form never
 * rides along with a staffing change. The status sits in the header instead, because it is the
 * one switch that starts and stops the dialer and it should not be three clicks deep.
 *
 * The tab is in the URL (`?tab=lists`), so a reload or a shared link lands where the reader was.
 * Supervisors read every tab; only an admin changes anything — core-api enforces that, and the
 * controls follow it.
 */

const TAB_LABELS: Record<CampaignTab, string> = {
  routing: "Routing",
  staffing: "Staffing",
  dispositions: "Dispositions",
  dialing: "Dialing",
  cadence: "Cadence",
  lists: "Lists",
};

export function CampaignPage() {
  const { campaignId } = useParams();
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab") ?? "";
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const { data: campaign, isPending, error, refetch } = useCampaign(campaignId);
  // What this campaign shows (ADR-060). A link to a tab it does not show — a bookmark from
  // before someone labelled it inbound — lands on Routing rather than on nothing.
  const tabs = campaign ? visibleTabs(campaign) : CAMPAIGN_TABS;
  const tab: CampaignTab = (tabs as readonly string[]).includes(requested)
    ? (requested as CampaignTab)
    : "routing";
  const update = useUpdateCampaign();
  const remove = useDeleteCampaign();
  const [editing, setEditing] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  if (isPending) {
    return (
      <Page>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      </Page>
    );
  }

  if (error || !campaign) {
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <Page>
        {missing ? (
          <EmptyState
            className="min-h-[60vh]"
            title="Campaign not found"
            description="It may have been deleted, or it belongs to another organisation."
            action={
              <Button variant="outline" asChild>
                <Link to="/campaigns">Back to campaigns</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState
            className="min-h-[60vh]"
            error={error}
            title="Could not load the campaign"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        )}
      </Page>
    );
  }

  const status = statusText(campaign.status);

  const changeStatus = (next: string) => {
    update.mutate(
      { id: campaign.id, status: next },
      {
        onSuccess: () =>
          toast.success(`${statusText(next).label}`, `${campaign.name}: ${statusText(next).description}`),
        onError: (e) =>
          toast.error(
            "Could not change the status",
            e instanceof ApiError ? e.message : "Please try again.",
          ),
      },
    );
  };

  return (
    <Page>
      <Link
        to="/campaigns"
        className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Campaigns
      </Link>

      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {campaign.name}
            <Badge tone={status.tone}>{status.label}</Badge>
            {!campaign.enabled && <Badge tone="warning">Disabled</Badge>}
          </span>
        }
        description={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1 font-mono text-xs">
              {campaign.slug}
              <CopyButton value={campaign.slug} label="Copy slug" />
            </span>
            <span>
              {campaign.state ?? "No state"} · {campaign.language === "es" ? "Spanish" : "English"}
            </span>
            <span>
              {campaign.did_count} number{campaign.did_count === 1 ? "" : "s"}
            </span>
            <span>Dialer: {dialModeText(campaign.dial_mode).label}</span>
            {user?.role === "superadmin" && campaign.tenant_name && <span>{campaign.tenant_name}</span>}
          </span>
        }
        action={
          canManage && (
            <>
              <Select value={campaign.status} onValueChange={changeStatus} disabled={update.isPending}>
                <SelectTrigger className="w-32" aria-label="Status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  {CAMPAIGN_STATUSES.map((s) => (
                    <SelectItem key={s} value={s} description={statusText(s).description}>
                      {statusText(s).label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Settings2 />
                Edit details
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="More actions">
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem destructive onSelect={() => setDeleting(true)}>
                    <Trash2 />
                    Delete campaign
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )
        }
      />

      {dialingContradictsPurpose(campaign) && (
        <Callout tone="warning">
          This campaign is marked {purposeText(campaign.purpose).label.toLowerCase()}, but its
          dialer is set to {dialModeText(campaign.dial_mode).label.toLowerCase()} — so it is
          calling this campaign&apos;s lists. The Dialing and Lists tabs stay below because of it.
          Set the mode to Off, or mark the campaign outbound.
        </Callout>
      )}

      <Tabs
        value={tab}
        onValueChange={(next) => setParams(next === "routing" ? {} : { tab: next }, { replace: true })}
      >
        <TabsList>
          {tabs.map((name) => (
            <TabsTrigger key={name} value={name}>
              {TAB_LABELS[name]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="routing" className="space-y-4 pt-4">
          <CampaignLines campaign={campaign} canManage={canManage} />
          <CampaignRepNumbers campaign={campaign} canManage={canManage} />
        </TabsContent>
        <TabsContent value="staffing" className="pt-4">
          <CampaignStaffing campaign={campaign} canManage={canManage} />
        </TabsContent>
        <TabsContent value="dispositions" className="pt-4">
          <CampaignDispositions campaign={campaign} canManage={canManage} />
        </TabsContent>
        {tabs.includes("dialing") && (
          <TabsContent value="dialing" className="pt-4">
            <CampaignDialing campaign={campaign} canManage={canManage} />
          </TabsContent>
        )}
        {tabs.includes("cadence") && (
          <TabsContent value="cadence" className="pt-4">
            <CampaignCadence campaign={campaign} canManage={canManage} />
          </TabsContent>
        )}
        {tabs.includes("lists") && (
          <TabsContent value="lists" className="pt-4">
            <CampaignLists campaign={campaign} canManage={canManage} />
          </TabsContent>
        )}
      </Tabs>

      <CampaignFormDialog open={editing} onOpenChange={setEditing} campaign={campaign} />

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title="Delete this campaign?"
        description={
          <>
            <span className="font-medium">{campaign.name}</span> and its lines will be removed.
            Numbers routed to it, and reps signed in to its lines, block this.
          </>
        }
        confirmLabel="Delete campaign"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(campaign.id);
            toast.success("Campaign deleted", campaign.name);
            navigate("/campaigns", { replace: true });
          } catch (e) {
            toast.error("Could not delete", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Page>
  );
}
